import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { formSchema } from './schema'
import DataDetail from './DataDetail'
import ProcessFormFields from './ProcessFormFields'
import {
  getEnrichmentTypes,
  getProcess as getProcessQuery,
  createProcess as createProcessMutation,
  editProcess as editProcessMutation,
} from '@/lib/processQueries'
import { useLazyQuery, useMutation } from '@apollo/client'
import { basicEnrichment, initialProcessData, interestConflictEnrichment } from './constants'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  usePredefinedToasts,
  useToastNotification,
} from '@/components/compliance-assistant/ToastNotification'

interface ProcessesProps {
  mode: 'create' | 'edit' | 'view'
}

export default function Processes({ mode }: ProcessesProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { uuid } = location.state || {}

  const [processData, setProcessData] = useState<FormProcessData>(initialProcessData)
  const [enrichmentTypes, setEnrichmentTypes] = useState<EnrichmentType[] | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)

  const [getEnrichments] = useLazyQuery(getEnrichmentTypes)
  const [getProcess] = useLazyQuery(getProcessQuery, { fetchPolicy: 'network-only' })
  const [createProcess] = useMutation(createProcessMutation)
  const [editProcess] = useMutation(editProcessMutation)

  const { validationErrorToast, networkErrorToast, genericErrorToast } = usePredefinedToasts()
  const { triggerToast } = useToastNotification()

  const loadData = () => {
    if (uuid) {
      getProcess({
        variables: {
          uuid,
        },
      })
        .then((res) => {
          const process = res.data.getProcess.process
          form.setValue('client_name', process.clientName)
          form.setValue('name', process.name)

          // Restore multi-selection; fall back to legacy single ID
          const requestedIds = process.data?.[0]?.requestedDownloadableTypeIds
          if (requestedIds && requestedIds.length > 0) {
            form.setValue('downloadable_types', requestedIds, { shouldValidate: true })
          } else {
            form.setValue('downloadable_types', [process.downloadableType.id], {
              shouldValidate: true,
            })
          }

          if (process.data.length === 1) {
            form.setValue('enrichment_type', process.data[0].enrichmentType.id)
            form.setValue('level_a', process.data[0].levelA)
            form.setValue('level_b', process.data[0].levelB)
            setProcessData((prev) => ({
              ...prev,
              ruts_a: process.data[0].rutsA,
              ruts_b: process.data[0].rutsB,
            }))
          }
        })
        .catch(() => {
          networkErrorToast()
        })
    }
  }

  useEffect(() => {
    // Redirect if accessing through the URL.
    if ((mode === 'edit' || mode === 'view') && !uuid) {
      navigate('/procesamientos')
    }

    setLoading(true)
    loadData() // If uuid is present, load the process data
    getEnrichments()
      .then((res) => {
        setEnrichmentTypes(res.data.getEnrichmentsTypes.enrichmentTypes)
        setLoading(false)
      })
      .catch(() => {
        networkErrorToast()
      })
  }, [])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      client_name: '',
      name: '',
      enrichment_type: undefined,
      downloadable_types: [],
      level_a: undefined,
      level_b: undefined,
    },
  })

  const enrichmentTypeValue = form.watch('enrichment_type')
  const enrichmentTypeKey = enrichmentTypes?.find(
    (type) => type.id === enrichmentTypeValue
  )?.naturalKey

  const resetProcessData = () => {
    setProcessData(initialProcessData)
  }

  const isValidTableForm = () => {
    if (enrichmentTypeKey === basicEnrichment) {
      return processData.ruts_a.length > 0
    } else if (enrichmentTypeKey === interestConflictEnrichment) {
      return (
        processData?.ruts_a &&
        processData.ruts_a.length > 0 &&
        processData?.ruts_b &&
        processData.ruts_b.length > 0
      )
    }
    return false
  }

  const onSubmit = async (values: z.infer<typeof formSchema>, shouldProcess: boolean) => {
    if (!isValidTableForm()) {
      validationErrorToast()
      return
    }

    try {
      const variables = {
        clientName: values.client_name,
        name: values.name,
        enrichmentTypeId: values.enrichment_type,
        downloadableTypeIds: values.downloadable_types,
        shouldProcess,
        rutsA: processData.ruts_a,
        rutsB: processData.ruts_b,
        levelA: values.level_a,
        levelB: values.level_b,
      }
      if (mode === 'create') {
        await createProcess({ variables })
      } else if (mode === 'edit') {
        await editProcess({ variables: { ...variables, processUuid: uuid } })
      }
      triggerToast({
        message:
          'Los datos ingresados se están procesando, favor esperar a que termine para poder visualizar el resultado.',
        duration: 5000,
        backgroundColor: '#C0E1F4',
        color: '#000000',
      })
      navigate('/procesamientos')
    } catch (error) {
      console.error('Error creating process:', error)
      genericErrorToast()
    }
  }

  const checkDisabled = () => {
    return form.formState.isSubmitting || !form.formState.isValid || !isValidTableForm()
  }

  return (
    <div aria-label="processes-container" className="container">
      <h1 className="title">Nuevo procesamiento</h1>
      <div className="subtitle w-full md:w-4/5 lg:w-3/5 xl:w-1/2">
        A continuación, podrás crear un nuevo procesamiento completando los campos requeridos y
        configurando tanto el procesamiento como su exportación.
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <p>Cargando...</p>
        </div>
      ) : (
        <Form {...form}>
          <form className="space-y-4 lg:w-1/3 md:w-2/5 sm:w-1/2 pt-4">
            <ProcessFormFields
              form={form}
              enrichmentTypes={enrichmentTypes}
              resetProcessData={resetProcessData}
              isViewMode={mode === 'view'}
            />
          </form>
          <div
            className={`space-y-4 ${
              enrichmentTypeKey === interestConflictEnrichment ? 'lg:w-3/4' : 'lg:w-1/3'
            } md:w-2/5 sm:w-1/2`}
          >
            <DataDetail
              processData={processData}
              setProcessData={setProcessData}
              enrichmentType={enrichmentTypeValue}
              enrichmentTypeKey={enrichmentTypeKey}
              isViewMode={mode === 'view'}
            />
            {mode !== 'view' && (
              <div className="flex" aria-label="processes-buttons">
                <Button
                  type="button"
                  variant="outline"
                  className="ml-auto mr-2"
                  onClick={() => form.handleSubmit((values) => onSubmit(values, false))()}
                  disabled={checkDisabled()}
                >
                  Guardar
                </Button>
                <Button
                  type="button"
                  className="bg-black-1 text-white"
                  onClick={() => form.handleSubmit((values) => onSubmit(values, true))()}
                  disabled={checkDisabled()}
                >
                  Procesar
                </Button>
              </div>
            )}
          </div>
        </Form>
      )}
    </div>
  )
}
